import { Button } from '@/components/Button';
import { StarRating } from '@/components/StarRating';
import { Text } from '@/components/Text';
import { TextArea } from '@/components/TextArea';
import { CreateEventFeedbackForm } from '@/types/feedback';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

export interface EventFeedbackFormProps {
  onSubmit: (
    values: CreateEventFeedbackForm,
    formikHelpers: FormikHelpers<CreateEventFeedbackForm>,
  ) => Promise<void>;
  isLoading: boolean;
  initialValues?: CreateEventFeedbackForm;
}

export const EventFeedbackForm = ({
  onSubmit,
  isLoading,
  initialValues = { rating: 0, comment: '' },
}: EventFeedbackFormProps) => {
  const { t } = useTranslation('eventFeedback');

  const validationSchema = yup.object().shape({
    rating: yup
      .number()
      .min(1, t('validation.rating.min'))
      .max(5, t('validation.rating.max'))
      .required(t('validation.rating.required')),
    comment: yup.string().max(1000, t('validation.comment.max')).notRequired(),
  });

  const handleSubmit = async (
    values: CreateEventFeedbackForm,
    formikHelpers: FormikHelpers<CreateEventFeedbackForm>,
  ) => {
    try {
      await onSubmit(values, formikHelpers);
    } catch (error) {
      handleError(error, formikHelpers.setErrors);
    }
  };

  return (
    <div className="w-full max-w-2xl">
      <div className="mb-6 text-center">
        <Text className="text-2xl font-bold text-sky-700">{t('title')}</Text>
        <Text className="mt-2 text-gray-600">{t('subtitle')}</Text>
        <div className="mx-auto mt-2 h-0.5 w-20 rounded-full bg-sky-500"></div>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, values, setFieldValue, isSubmitting }) => (
          <Form className="space-y-6">
            {/* Rating Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                {t('form.rating.label')}
                <span className="ml-1 text-red-500">*</span>
              </label>
              <div className="flex flex-col items-center space-y-2">
                <StarRating
                  value={values.rating}
                  onChange={rating => setFieldValue('rating', rating)}
                  size="lg"
                  error={!!touched.rating && !!errors.rating}
                  className="justify-center"
                />
                <Text className="text-sm text-gray-500">
                  {t('form.rating.placeholder')}
                </Text>
                {touched.rating && errors.rating && (
                  <Text className="text-sm text-red-500">{errors.rating}</Text>
                )}
              </div>
            </div>

            {/* Comment Section */}
            <div className="space-y-1">
              <TextArea
                label={t('form.comment.label')}
                name="comment"
                placeholder={t('form.comment.placeholder')}
                error={!!touched.comment && !!errors.comment}
                rows={4}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{values.comment?.length || 0}/1000</span>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={isSubmitting || isLoading}
                className="w-full rounded-full bg-linko-light hover:bg-linko-dark"
              >
                {isSubmitting || isLoading
                  ? t('form.submitting')
                  : t('form.submit')}
              </Button>
              <Text className="mt-2 text-center text-sm font-light text-gray-500">
                {t('form.rating.required')}
              </Text>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
